import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Button } from '../ui';
import type { FamilyMember } from '../../types';

export interface FamilyMemberCardProps {
  familyMember: FamilyMember;
  onEdit: (familyMember: FamilyMember) => void;
  onDelete: (id: string) => void;
  getRelationshipLabel: (relationship: string) => string;
  calculateAge: (dateOfBirth: string) => number;
  compact?: boolean;
}

export const FamilyMemberCard: React.FC<FamilyMemberCardProps> = ({
  familyMember,
  onEdit,
  onDelete,
  getRelationshipLabel,
  calculateAge,
  compact = false
}) => {
  const fullName =
    familyMember.name && familyMember.lastname
      ? `${familyMember.name} ${familyMember.lastname}`.trim()
      : familyMember.name || familyMember.lastname || `Familiar ${familyMember.cedula || familyMember.id}`;

  return (
    <div
      className={clsx(
        'rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md',
        familyMember.hasSeparateFamily
          ? 'border-rose-200 bg-rose-50/50 ring-1 ring-rose-100/80'
          : 'border-slate-200/90 bg-white ring-1 ring-slate-100/80'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className={clsx('font-semibold text-slate-900', compact ? 'text-sm' : 'text-base')}>{fullName}</p>
          <span className="mt-1.5 inline-flex rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200/80">
            {getRelationshipLabel(familyMember.relationship)}
          </span>
        </div>
        <div className="flex shrink-0 gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(familyMember)}
            className="text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Editar familiar"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(familyMember.id)}
            className="text-rose-500 hover:bg-rose-50 hover:text-rose-700"
            aria-label="Eliminar familiar"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <dl className={clsx('mt-3 grid gap-2 text-sm', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
        {familyMember.cedula && (
          <div>
            <dt className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Cédula</dt>
            <dd className="font-mono text-slate-800">{familyMember.cedula}</dd>
          </div>
        )}
        {(familyMember.age || familyMember.dateOfBirth) && (
          <div>
            <dt className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Edad</dt>
            <dd className="font-medium text-slate-800">
              {familyMember.age ?? (familyMember.dateOfBirth ? calculateAge(familyMember.dateOfBirth) : '—')} años
            </dd>
          </div>
        )}
      </dl>
      {familyMember.hasSeparateFamily && (
        <p className="mt-2 text-xs font-semibold text-rose-700">Familia aparte — no cubierto por el plan</p>
      )}
      {familyMember.observacion && (
        <p className="mt-2 border-t border-slate-100 pt-2 text-xs italic text-slate-600">{familyMember.observacion}</p>
      )}
      <p className="mt-2 text-[11px] font-medium text-slate-400">
        Registrado: {new Date(familyMember.createdAt).toLocaleDateString('es')}
      </p>
    </div>
  );
};
